import { useMemo } from "react";

export const PowerTriangle = ({ realPower, reactivePower, apparentPower, powerFactor }) => {
  const triangleData = useMemo(() => {
    // Normalize values for display
    const maxVal = Math.max(realPower, reactivePower, apparentPower);
    if (maxVal === 0) return null;
    
    const scale = 100 / maxVal;
    const p = realPower * scale;
    const q = reactivePower * scale;
    
    // Calculate angle (theta = arccos(PF))
    const theta = Math.acos(Math.min(powerFactor, 1)) * (180 / Math.PI);
    
    return { p, q, theta };
  }, [realPower, reactivePower, apparentPower, powerFactor]);

  // Format power for display
  const formatPower = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(2)} MW`;
    if (value >= 1000) return `${(value / 1000).toFixed(2)} kW`;
    return `${value.toFixed(2)} W`;
  };

  if (!triangleData) {
    return (
      <div className="p-4 rounded-lg bg-secondary/30 border border-border">
        <p className="text-sm text-muted-foreground text-center">
          Enter values to see power triangle
        </p>
      </div>
    );
  }

  const { p, q, theta } = triangleData;
  
  // SVG dimensions
  const width = 280;
  const height = 160;
  const padding = 40;
  
  // Triangle points (origin at bottom-left)
  const originX = padding;
  const originY = height - padding;
  const pEndX = originX + (p / 100) * (width - 2 * padding);
  const qEndY = originY - (q / 100) * (height - 2 * padding);

  return (
    <div className="p-4 rounded-lg bg-secondary/30 border border-border">
      <h4 className="text-sm font-medium text-foreground mb-3">Power Triangle</h4>
      
      <svg 
        viewBox={`0 0 ${width} ${height}`} 
        className="w-full max-w-[280px] mx-auto"
        style={{ overflow: 'visible' }}
      >
        {/* Grid lines */}
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path 
              d="M 20 0 L 0 0 0 20" 
              fill="none" 
              stroke="hsl(var(--border))" 
              strokeWidth="0.5"
              opacity="0.3"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {/* Real Power (P) - Horizontal green line */}
        <line
          x1={originX}
          y1={originY}
          x2={pEndX}
          y2={originY}
          stroke="hsl(var(--power-real))"
          strokeWidth="3"
          strokeLinecap="round"
          className="power-triangle-path"
        />
        
        {/* Reactive Power (Q) - Vertical blue line */}
        <line
          x1={pEndX}
          y1={originY}
          x2={pEndX}
          y2={qEndY}
          stroke="hsl(var(--power-reactive))"
          strokeWidth="3"
          strokeLinecap="round"
          className="power-triangle-path"
          style={{ animationDelay: '0.2s' }}
        />
        
        {/* Apparent Power (S) - Diagonal orange line */}
        <line
          x1={originX}
          y1={originY}
          x2={pEndX}
          y2={qEndY}
          stroke="hsl(var(--power-apparent))"
          strokeWidth="3"
          strokeLinecap="round"
          className="power-triangle-path"
          style={{ animationDelay: '0.4s' }}
        />

        {/* Angle arc */}
        <path
          d={`M ${originX + 25} ${originY} A 25 25 0 0 0 ${originX + 25 * Math.cos(theta * Math.PI / 180)} ${originY - 25 * Math.sin(theta * Math.PI / 180)}`}
          fill="none"
          stroke="hsl(var(--muted-foreground))"
          strokeWidth="1.5"
          strokeDasharray="3 2"
        />

        {/* Labels */}
        {/* P label */}
        <text
          x={(originX + pEndX) / 2}
          y={originY + 18}
          textAnchor="middle"
          className="fill-power-real text-[10px] font-medium"
        >
          P (Real)
        </text>
        
        {/* Q label */}
        <text
          x={pEndX + 12}
          y={(originY + qEndY) / 2}
          textAnchor="start"
          className="fill-power-reactive text-[10px] font-medium"
        >
          Q
        </text>
        
        {/* S label */}
        <text
          x={(originX + pEndX) / 2 - 15}
          y={(originY + qEndY) / 2 - 5}
          textAnchor="middle"
          className="fill-power-apparent text-[10px] font-medium"
          transform={`rotate(-${theta}, ${(originX + pEndX) / 2 - 15}, ${(originY + qEndY) / 2 - 5})`}
        >
          S (Apparent)
        </text>

        {/* Angle label */}
        <text
          x={originX + 35}
          y={originY - 8}
          textAnchor="start"
          className="fill-muted-foreground text-[9px]"
        >
          θ = {theta.toFixed(1)}°
        </text>

        {/* Points */}
        <circle cx={originX} cy={originY} r="4" fill="hsl(var(--foreground))" />
        <circle cx={pEndX} cy={originY} r="4" fill="hsl(var(--power-real))" />
        <circle cx={pEndX} cy={qEndY} r="4" fill="hsl(var(--power-apparent))" />
      </svg>

      {/* Legend */}
      <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-power-real" />
          <span className="text-muted-foreground">P: {formatPower(realPower)}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-power-reactive" />
          <span className="text-muted-foreground">Q: {formatPower(reactivePower).replace('W', 'VAR')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-power-apparent" />
          <span className="text-muted-foreground">S: {formatPower(apparentPower).replace('W', 'VA')}</span>
        </div>
      </div>
    </div>
  );
};
